import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ColumnDef } from '@tanstack/react-table';
import { api } from '../lib/api';
import { Badge, Button } from '../atoms';
import { PageHeader } from '../shell';
import { DataTable } from '../organisms';

/**
 * CommissionLedger — /v2/commissions/ledger
 *
 * The earned-commission ledger (feature #43). Reads the role-aware
 * GET /api/commissions surface:
 *   • a firm-wide role (super_admin/admin/administrator) sees ALL consultants'
 *     commissions (grouped/summed per consultant + the entry detail);
 *   • an agent/analyst/consultant sees ONLY their OWN commissions.
 * The money-blind `administrator` gets the same shape with the dollar figures
 * redacted by the response boundary (they render as "—").
 *
 * Commissions are BOOKED automatically as invoices are paid — this surface is
 * read-only. Reached from the Consultant deals page ("View commission ledger").
 */

type Scope = 'all' | 'own';

interface CommissionEntry {
  id: string;
  consultant_contact_id: string | null;
  consultant_name: string | null;
  consultant_deal_id: string | null;
  invoice_id: string | null;
  invoice_number: string | null;
  invoice_payment_id: string | null;
  amount: number | string | null;
  basis: 'percent' | 'flat' | null;
  status: 'earned' | 'paid_out' | 'void' | null;
  earned_at: string | null;
}

interface CommissionSummaryRow {
  consultant_id: string | null;
  consultant_name: string | null;
  entry_count: number | string | null;
  earned_amount: number | string | null;
  paid_out_amount: number | string | null;
  total_amount: number | string | null;
}

interface CommissionResponse {
  scope: Scope;
  entries: CommissionEntry[];
  summary: CommissionSummaryRow[];
}

/** Format a money value; a redacted (undefined/null) figure renders as an em-dash. */
function money(v: number | string | null | undefined): string {
  if (v === null || v === undefined || v === '') return '—';
  const n = Number(v);
  if (Number.isNaN(n)) return String(v);
  return `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function formatDate(v: string | null): string {
  if (!v) return '—';
  const day = v.slice(0, 10);
  const d = new Date(`${day}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return day;
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric', timeZone: 'UTC' });
}

const STATUS_VARIANT: Record<string, 'success' | 'info' | 'neutral'> = {
  earned: 'info',
  paid_out: 'success',
  void: 'neutral',
};

function statusLabel(s: string | null): string {
  if (!s) return '—';
  return s === 'paid_out' ? 'Paid out' : s.charAt(0).toUpperCase() + s.slice(1);
}

export function CommissionLedger() {
  const navigate = useNavigate();

  const query = useQuery<CommissionResponse>({
    queryKey: ['commissions'],
    queryFn: ({ signal }) => api.get<CommissionResponse>('/api/commissions', signal),
  });

  const data = query.data;
  const scope: Scope = data?.scope ?? 'own';
  const entries = useMemo(() => data?.entries ?? [], [data]);
  const summary = useMemo(() => data?.summary ?? [], [data]);

  const summaryColumns = useMemo<ColumnDef<CommissionSummaryRow>[]>(() => [
    {
      accessorKey: 'consultant_name',
      header: 'Consultant',
      cell: ({ row }) => (
        <span style={{ fontWeight: 'var(--fw-medium)', color: 'var(--color-ink)' }}>
          {row.original.consultant_name || row.original.consultant_id || '—'}
        </span>
      ),
    },
    { accessorKey: 'entry_count', header: 'Entries', cell: ({ getValue }) => Number(getValue() ?? 0).toLocaleString() },
    { accessorKey: 'earned_amount', header: 'Earned', cell: ({ getValue }) => money(getValue() as number | string | null) },
    { accessorKey: 'paid_out_amount', header: 'Paid out', cell: ({ getValue }) => money(getValue() as number | string | null) },
    {
      accessorKey: 'total_amount',
      header: 'Total',
      cell: ({ getValue }) => (
        <span style={{ fontWeight: 'var(--fw-semibold)', color: 'var(--color-ink)' }}>
          {money(getValue() as number | string | null)}
        </span>
      ),
    },
  ], []);

  const entryColumns = useMemo<ColumnDef<CommissionEntry>[]>(() => [
    {
      accessorKey: 'consultant_name',
      header: 'Consultant',
      cell: ({ row }) => row.original.consultant_name || row.original.consultant_contact_id || '—',
    },
    {
      accessorKey: 'invoice_number',
      header: 'Invoice',
      cell: ({ row }) => {
        const num = row.original.invoice_number;
        const id = row.original.invoice_id;
        if (!num && !id) return '—';
        return (
          <a
            href={`/invoices/${id}`}
            onClick={(e) => { e.preventDefault(); if (id) navigate(`/invoices/${id}`); }}
            style={{ fontFamily: 'var(--font-mono)', color: 'var(--brand-primary)' }}
          >
            {num || id}
          </a>
        );
      },
    },
    {
      accessorKey: 'basis',
      header: 'Basis',
      cell: ({ getValue }) => {
        const b = getValue() as string | null;
        return <Badge variant={b === 'flat' ? 'warning' : 'info'}>{b === 'flat' ? 'Flat' : 'Percent'}</Badge>;
      },
    },
    {
      accessorKey: 'amount',
      header: 'Commission',
      cell: ({ getValue }) => (
        <span style={{ color: 'var(--color-ink)' }}>{money(getValue() as number | string | null)}</span>
      ),
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ getValue }) => {
        const s = getValue() as string | null;
        return <Badge variant={STATUS_VARIANT[s ?? ''] ?? 'neutral'} dot>{statusLabel(s)}</Badge>;
      },
    },
    { accessorKey: 'earned_at', header: 'Earned', cell: ({ getValue }) => formatDate(getValue() as string | null) },
  ], [navigate]);

  const subtitle = query.isLoading
    ? 'Loading…'
    : scope === 'all'
      ? `All consultants · ${entries.length} entr${entries.length === 1 ? 'y' : 'ies'}`
      : `My commissions · ${entries.length} entr${entries.length === 1 ? 'y' : 'ies'}`;

  return (
    <>
      <PageHeader
        title="Commission ledger"
        subtitle={subtitle}
        actions={
          <Button variant="secondary" size="md" onClick={() => navigate('/consultant-deals')}>
            Consultant deals
          </Button>
        }
      />

      {query.isError && (
        <div style={{ color: 'var(--color-status-red)', marginBottom: 'var(--sp-4)' }}>
          Could not load commissions.
        </div>
      )}

      <section style={{ marginBottom: 'var(--sp-6)' }}>
        <h2 style={{ fontSize: 'var(--fs-lg)', fontWeight: 'var(--fw-semibold)', color: 'var(--color-ink)', margin: '0 0 var(--sp-3)' }}>
          Per-consultant summary
        </h2>
        <DataTable
          data={summary}
          columns={summaryColumns}
          emptyMessage={query.isLoading ? 'Loading…' : 'No commissions booked yet. Commissions are earned automatically as invoices are paid.'}
        />
      </section>

      <section>
        <h2 style={{ fontSize: 'var(--fs-lg)', fontWeight: 'var(--fw-semibold)', color: 'var(--color-ink)', margin: '0 0 var(--sp-3)' }}>
          Ledger entries
        </h2>
        <DataTable
          data={entries}
          columns={entryColumns}
          emptyMessage={query.isLoading ? 'Loading…' : 'No commission entries yet.'}
        />
      </section>
    </>
  );
}
